/**
 * derivative.ts — Section 2: "Differentiation (Zooming in to the Instant)".
 *
 * A graph of f(x) = x² with two points on it: Point A fixed at x=2, Point B
 * at x = 2 + Δx. As the Δx slider moves toward zero, the secant line through
 * A and B becomes the tangent at A. The slope equation and the "average vs
 * instantaneous" status line update live (re-typeset via math.ts).
 *
 * Direct port of the draft's derivative-canvas logic, cleaned up for strict
 * TypeScript: typed canvas context, no implicit any, `const` where the draft
 * used `let`, and the MathJax call routed through math.ts instead of the raw
 * global.
 */

import { render } from './math';

// The function under study. Hardcoded to y = x² — the draft's choice, and the
// simplest non-linear function whose derivative is itself a clean line.
const f = (x: number): number => x * x;
// Point A is fixed at x = 2 (y = 4). Point B slides with Δx.
const FIXED_X = 2;

const TANGENT_THRESHOLD = 0.05; // Δx below this → "instantaneous" state

export function initDerivative(): void {
  const canvas = document.getElementById('derivativeCanvas') as HTMLCanvasElement | null;
  const sliderEl = document.getElementById('dxSlider') as HTMLInputElement | null;
  const dxValueEl = document.getElementById('dxValue');
  const bxDisplayEl = document.getElementById('bx');
  const byDisplayEl = document.getElementById('by');
  const slopeEqEl = document.getElementById('slopeEq');
  const statusEl = document.getElementById('derivativeStatus');
  if (!canvas || !sliderEl || !dxValueEl || !bxDisplayEl || !byDisplayEl || !slopeEqEl || !statusEl) return;

  // Narrowed, non-null aliases so nested closures (drawAxes / drawCurve /
  // update) don't re-trigger "possibly null" — TS won't carry the guard into a
  // function body that outlives the check.
  const slider = sliderEl;
  const dxValue = dxValueEl;
  const bxDisplay = bxDisplayEl;
  const byDisplay = byDisplayEl;
  const slopeEq = slopeEqEl;
  const status = statusEl;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const W = canvas.width;
  const H = canvas.height;

  // The draft maps x ∈ [0, 10] across the canvas width, y ∈ [0, 100] up it.
  const mapX = (x: number): number => (x / 10) * W;
  const mapY = (y: number): number => H - (y / 100) * H;

  function drawAxes(): void {
    ctx!.beginPath();
    ctx!.strokeStyle = '#e2e8f0';
    ctx!.lineWidth = 1;
    for (let i = 0; i <= 100; i += 20) {
      ctx!.moveTo(0, mapY(i));
      ctx!.lineTo(W, mapY(i));
    }
    for (let i = 0; i <= 10; i += 2) {
      ctx!.moveTo(mapX(i), 0);
      ctx!.lineTo(mapX(i), H);
    }
    ctx!.stroke();
  }

  function drawCurve(): void {
    ctx!.beginPath();
    ctx!.strokeStyle = '#94a3b8'; // slate-400
    ctx!.lineWidth = 3;
    for (let x = 0; x <= 10; x += 0.1) {
      const cx = mapX(x);
      const cy = mapY(f(x));
      if (x === 0) ctx!.moveTo(cx, cy);
      else ctx!.lineTo(cx, cy);
    }
    ctx!.stroke();
  }

  async function update(): Promise<void> {
    const dx = parseFloat(slider.value);
    const x2 = FIXED_X + dx;
    const y1 = f(FIXED_X);
    const y2 = f(x2);

    ctx!.clearRect(0, 0, W, H);
    drawAxes();
    drawCurve();

    const dy = y2 - y1;
    const slope = dy / dx;

    // Secant (or tangent, when Δx is tiny). Extended across the whole canvas.
    const tangent = dx <= TANGENT_THRESHOLD;
    ctx!.beginPath();
    ctx!.strokeStyle = tangent ? '#10b981' : '#3b82f6'; // emerald-500 / blue-500
    ctx!.lineWidth = 2;
    const lineStartY = slope * (0 - FIXED_X) + y1;
    const lineEndY = slope * (10 - FIXED_X) + y1;
    ctx!.moveTo(mapX(0), mapY(lineStartY));
    ctx!.lineTo(mapX(10), mapY(lineEndY));
    ctx!.stroke();

    // Point A (red, fixed).
    ctx!.fillStyle = '#ef4444';
    ctx!.beginPath();
    ctx!.arc(mapX(FIXED_X), mapY(y1), 5, 0, Math.PI * 2);
    ctx!.fill();

    // Point B (green when it merges with A, amber otherwise).
    ctx!.fillStyle = tangent ? '#10b981' : '#f59e0b';
    ctx!.beginPath();
    ctx!.arc(mapX(x2), mapY(y2), 5, 0, Math.PI * 2);
    ctx!.fill();

    // Live numeric readouts.
    dxValue.textContent = dx.toFixed(2);
    bxDisplay.textContent = x2.toFixed(2);
    byDisplay.textContent = y2.toFixed(2);

    // Live equation. Re-typeset via math.ts (handles MathJax startup race).
    const tex = `$$ m = \\frac{${y2.toFixed(2)} - ${y1.toFixed(2)}}{${x2.toFixed(2)} - ${FIXED_X.toFixed(2)}} = ${slope.toFixed(2)} $$`;
    void render(slopeEq, tex);

    if (tangent) {
      status.textContent =
        'Point B is basically on top of Point A! You found the Instantaneous Rate of Change (The Derivative).';
      status.className = 'mt-3 text-sm font-bold text-emerald-600';
    } else {
      status.textContent =
        'This is currently an average rate of change between two distinct points (Regression).';
      status.className = 'mt-3 text-sm font-semibold text-amber-600';
    }
  }

  slider.addEventListener('input', () => void update());
  void update();
}
